import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Mail, Lock, Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import PortalSwitcher from "@/components/PortalSwitcher";
import { biometric as mobileBiometric } from "@/mobile";
import DriverDiagnosticsPanel from "@/debug/DriverDiagnosticsPanel"; // TEMP-DIAG
import { logDiag, tokenFingerprint } from "@/debug/diagJournal"; // TEMP-DIAG

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DriverLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Phase 3d — detect biometric availability on native bundles only.
  // The check is silent: on web it resolves to `false` and the button stays hidden.
  useEffect(() => {
    let cancelled = false;
    mobileBiometric.isAvailable().then((avail) => {
      if (!cancelled) setBiometricAvailable(!!avail);
    });
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/driver/login`, formData);

      // TEMP-DIAG: capture login response shape before storing anything
      logDiag("login:response", {
        status: response.status,
        dataType: typeof response.data,
        hasAccessToken: !!response.data?.access_token,
        keys: response.data && typeof response.data === "object"
          ? Object.keys(response.data).join(",").slice(0, 120)
          : String(response.data).slice(0, 80),
      });

      // Store token and driver info
      localStorage.setItem("driver_token", response.data.access_token);
      localStorage.setItem("driver", JSON.stringify(response.data.driver));

      // TEMP-DIAG: verify what actually landed in localStorage
      logDiag("login:token-stored", { auth: tokenFingerprint(localStorage.getItem("driver_token")) });

      // Phase 3d — opportunistically enroll biometric on native devices.
      // FIRE-AND-FORGET: do NOT `await` this. On iOS TestFlight the
      // `capacitor-native-biometric` plugin can hang indefinitely if the
      // Keychain access group / entitlement is misconfigured. Awaiting it
      // here would keep the login button stuck on "Logging in..." forever
      // even though the JWT is already stored and the user is authenticated.
      // Silently no-ops on web / when unavailable.
      mobileBiometric
        .enable({
          email: formData.email,
          jwt: response.data.access_token,
        })
        .catch(() => { /* never block login */ });

      toast.success("Login successful!");
      navigate("/driver/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometric = async () => {
    setBiometricBusy(true);
    try {
      const res = await mobileBiometric.tryUnlock();
      if (!res?.ok) {
        if (res?.reason !== "not_available") {
          toast.error("Biometric unlock failed", { description: res?.reason });
        }
        return;
      }
      // Persist the unlocked JWT into the same localStorage slot the rest of
      // the app reads. Email is restored too for the dashboard greeting.
      localStorage.setItem("driver_token", res.jwt);
      if (res.email) {
        // We don't have a full driver object here, but the dashboard refetches
        // /api/driver/me which populates the cache.
        localStorage.setItem("driver", JSON.stringify({ email: res.email }));
      }
      toast.success("Welcome back");
      navigate("/driver/dashboard");
    } finally {
      setBiometricBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <PortalSwitcher />
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white mb-4">
            <Truck className="h-10 w-10 text-orange-500" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              Transport Anything 247
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-white mt-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Driver Login
          </h1>
          <p className="text-slate-400 mt-2">Access your driver dashboard</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>
              Enter your credentials to access your driver account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="driver-login-email"
                    placeholder="driver@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    data-testid="driver-login-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="driver-login-submit"
                disabled={loading}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>

              {biometricAvailable && (
                <Button
                  type="button"
                  onClick={handleBiometric}
                  disabled={biometricBusy}
                  variant="outline"
                  className="w-full h-12 border-orange-200 text-orange-600 hover:bg-orange-50 font-semibold flex items-center justify-center gap-2"
                  data-testid="driver-biometric-unlock-btn"
                >
                  <Fingerprint className="h-5 w-5" />
                  {biometricBusy ? "Verifying..." : "Unlock with biometrics"}
                </Button>
              )}
            </form>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <div className="text-sm text-slate-500 text-center">
              Don&apos;t have an account?{" "}
              <Link to="/driver/register" className="text-orange-500 hover:underline font-medium">
                Register here
              </Link>
            </div>
          </CardFooter>
        </Card>
        {/* TEMP-DIAG: visible only when the journal has entries (native builds) */}
        <DriverDiagnosticsPanel />
      </div>
      </div>
    </div>
  );
}
